<template>
  <Dialog :visible="showDialog" modal
     :style="{ width: '75rem' }" class="mx-4">
    <div class="flex flex-col md:flex-row w-full justify-evenly"
      v-if="getSuperUserProfileInfo">
      <div class="flex flex-col justify-evenly px-5 gap-y-2 w-full md:w-1/2">
        <div class="flex flex-row items-center mb-4">
          <div class="w-16">
            <img :src="getProfilePicture" alt="Profile picture" class="rounded-full"/>
          </div>
          <div class="flex flex-col ml-1">
            <h2>{{ getSuperUserProfileInfo.name }}</h2>
            <div class="flex flex-row text-base items-center gap-x-1"
              v-if="getSuperUserProfileInfo.currentLevel">
              <font-awesome-icon icon="fa-star" class="text-[var(--color-orange)]"/>
              <p>{{ $t("SuperUserProfileOverlayComponent.level") }}:
                {{ getSuperUserProfileInfo.currentLevel }}</p>
            </div>
          </div>
        </div>
        <h3 class="text-gray-600">{{ $t("SuperUserProfileOverlayComponent.userInfo") }}</h3>
        <div class="flex flex-row items-center gap-x-1" v-if="getSuperUserProfileInfo.email">
          <font-awesome-icon icon="fa-regular fa-envelope"/>
          <p>{{ getSuperUserProfileInfo.email }}</p>
        </div>
        <div class="flex flex-row items-center gap-x-1" v-if="getSuperUserProfileInfo.mobileNumber">
          <font-awesome-icon icon="fa-solid fa-phone"/>
          <p>{{ getSuperUserProfileInfo.mobileNumber }}</p>
        </div>
        <Divider/>
        <h3 class="text-gray-600">
          {{ $t("SuperUserProfileOverlayComponent.lastActivity") }}
        </h3>
        <p>
          {{ getSuperUserProfileInfo.date_lastLogin ?
          formatDate(getSuperUserProfileInfo.date_lastLogin) : "-" }}
        </p>
        <Divider/>
        <h3 class="text-gray-600" v-if="getSuperUserProfileInfo.date_registered">
          {{ $t("SuperUserProfileOverlayComponent.memberSince") }}
        </h3>
        <p v-if="getSuperUserProfileInfo.date_registered">
          {{ formatDate(getSuperUserProfileInfo.date_registered) }}
        </p>
        <Divider/>
        <h3 class="text-gray-600">{{ $t("SuperUserProfileOverlayComponent.teams") }}</h3>
        <div class="flex flex-row flex-wrap items-center gap-1">
          <div v-for="team in getSuperUserProfileInfo.teams" :key="team">
            <Tag severity="warn" :value=team></Tag>
          </div>
        </div>
        <Divider/>
        <h3 class="text-gray-600">
          {{ $t("SuperUserProfileOverlayComponent.tags") }}
        </h3>
        <div class="flex flex-row flex-wrap items-center gap-1">
          <div v-for="tag in getSuperUserProfileTags" :key="tag">
            <Tag severity="warn" :value="tag.tag[lang]" rounded
            v-tooltip.top="tag.identifier[lang]"></Tag>
          </div>
        </div>
      </div>
      <div class="flex flex-col justify-evenly px-5 gap-y-2 w-full md:w-1/2">
        <h3>{{ $t("SuperUserProfileOverlayComponent.activities") }}</h3>
        <h4 class="text-gray-600">
          {{ $t("SuperUserProfileOverlayComponent.openTasks") }}
        </h4>
        <div class="flex flex-row flex-wrap items-center gap-1">
          <div v-for="task in getSuperUserProfileTasks" :key="task">
            <div @mouseover="showTaskHover(task)" @mouseout="hideTaskHover(task)"
              @focusin="showTaskHover(task)" @focusout="hideTaskHover(task)">
              <Tag severity="warn" :value=task.schemaName></Tag>
            </div>
            <div @mouseover="showTaskHover(task)" @mouseout="hideTaskHover(task)"
              @focusin="showTaskHover(task)" @focusout="hideTaskHover(task)"
              :class="showTasksHover.includes(task) ? 'card absolute z-50': 'hidden'">
              <div class="w-full border-[var(--color-orange)] pb-4 border-b leading-[17px]">
                <div>
                  <p v-if="task.name != ''">
                    <strong>{{ $t("SuperUserProfileOverlayComponent.name") }}:</strong>
                    {{ task.name }}
                  </p>
                  <p v-if="task.createdFrom != ''">
                    <strong>{{ $t("SuperUserProfileOverlayComponent.creator") }}: </strong>
                    {{ (/^[A-Za-z\s]+$/).test(task.createdFrom) ? task.createdFrom : "System" }}
                  </p>
                  <p v-if="task.description != ''">
                    <strong>{{ $t("SuperUserProfileOverlayComponent.description") }}:</strong>
                    {{ task.description }}
                  </p>
                </div>
              </div>
              <div class="w-full border-[var(--color-orange)] border-b overflow-auto max-h-16"
                v-if="task.taskEvents.find((t) => t.status == 'finished')">
                <div class="max-w-[90%] grid grid-cols-[155px_auto]"
                  v-for="taskEvent in task.taskEvents.filter((t) => t.status == 'finished')"
                  :key="taskEvent">
                  <p>{{ formatDate(taskEvent.date_eventBegin) }}</p>
                  <p>{{ taskEvent.status }}</p>
                </div>
              </div>
              <div class="w-full border-[var(--color-orange)] border-b overflow-auto max-h-16"
                v-if="task.taskEvents.find((t) => t.status == 'open')">
                <div class="max-w-[90%] grid grid-cols-[155px_auto]"
                  v-for="taskEvent in task.taskEvents.filter((t) => t.status == 'open')"
                  :key="taskEvent">
                  <p>{{ formatDate(taskEvent.date_eventBegin) }}</p>
                  <p>{{ taskEvent.status }}</p>
                </div>
              </div>
              <div class="w-full border-[var(--color-orange)] border-b overflow-auto max-h-16"
                v-if="task.taskEvents.find((t) => t.status == 'scheduled')">
                <div class="max-w-[90%] grid grid-cols-[155px_auto]"
                  v-for="taskEvent in task.taskEvents.filter((t) => t.status == 'scheduled')"
                  :key="taskEvent">
                  <p>{{ formatDate(taskEvent.date_eventBegin) }}</p>
                  <p>{{ taskEvent.status }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Divider/>
        <h4 class="text-gray-600">
          {{ $t("SuperUserProfileOverlayComponent.completedTasks") }}
        </h4>
        <div>
          <div v-for="completedTask in getSuperUserProfileCompletedTasks" :key="completedTask">
            <p v-if="completedTask?.assessmentName">
              {{ completedTask?.assessmentName }}:
              <strong>{{ formatDate(completedTask?.date_taskEnd) }}</strong>
            </p>
          </div>
        </div>
        <Divider/>
        <h4 class="text-gray-600" v-if="currentLevelProgress?.goals?.length">
          {{ $t("SuperUserProfileOverlayComponent.weeklyGoalsStatus") }}
        </h4>
        <div v-if="currentLevelProgress?.goals?.length">
          <div class="flex flex-row gap-x-1"
            v-for="goal in currentLevelProgress.goals" :key="goal.id">
            <p>{{ goal?.title?.[lang] || goal?.title?.en ||
              $t(`SuperUserProfileOverlayComponent.weeklyGoals.${goal?.id}`) }}:</p>
            <p :class="goal.progress >= goal.requiredCompletions ? 'text-[var(--color-orange)]'
              : 'text-gray-500'">
              <strong>{{ goal.progress }}</strong>
            </p>
            <p><strong>/{{ goal.requiredCompletions }}</strong></p>
          </div>
          <Divider/>
        </div>
        <h4 class="text-gray-600">
          {{ $t("SuperUserProfileOverlayComponent.workshopParticipation") }}
        </h4>
        <div>
          <div v-for="omindWorkshop in getSuperUserProfileOmindWorkshops"
            :key="omindWorkshop" class="flex flex-row gap-x-1">
            <p>{{ omindWorkshop.name }}:</p>
            <p :class="new Date(omindWorkshop.date_workshop) <= new Date() ? 'text-black'
              : 'text-gray-500'">
              <strong>{{ formatDate(omindWorkshop.date_workshop) }}</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="flex items-center justify-end">
      <Button icon="pi pi-trash" :label="$t('SuperUserProfileOverlayComponent.deleteUser')"
        class="rounded-full bg-red-600 text-white py-2 px-4 m-1"
        @click.prevent="deleteUser" unstyled />
    </div>
  </Dialog>
</template>

<script>
import defaultProfileImage from '@/assets/defaultProfileImage.js';
import {
  Button,
  Dialog,
  Divider,
  Tag,
} from 'primevue';
import { formatLocaleDate } from '@/helpers/formatLocaleDate';


export default {
  name: 'SuperUserProfileOverlayComponent',
  props: {
    data: Object,
    showDialog: Boolean,
  },
  components: {
    Dialog,
    Button,
    Divider,
    Tag,
  },
  data() {
    return {
      showTagIdentifier: [],
      showTasksHover: [],
      showPopup: false,
      popupMessage: this.$t('UserAdministrationComponent.popup.deleteMessage'),
      deleteButtonText: this.$t('UserAdministrationComponent.popup.deleteButton'),
      cancelButtonText: this.$t('UserAdministrationComponent.popup.cancelButton'),
    };
  },
  computed: {
    lang() {
      return this.$i18n.locale;
    },
    getProfilePicture() {
      if (this.getSuperUserProfileInfo.picture_profile?.small) {
        return this.getSuperUserProfileInfo.picture_profile.small;
      }

      return defaultProfileImage;
    },
    getSuperUserProfileInfo() {
      return this.$store.getters.superUserProfileInfo;
    },
    getSuperUserProfileTags() {
      return this.$store.getters.superUserProfileTags;
    },
    getSuperUserProfileTasks() {
      return this.$store.getters.superUserProfileTasks;
    },
    getSuperUserProfileCompletedTasks() {
      return this.$store.getters.superUserProfileCompletedTasks;
    },
    getSuperUserProfileOmindWorkshops() {
      return this.$store.getters.superUserProfileOmindWorkshops;
    },
    currentLevelProgress() {
      return this.$store.getters.superUserProfileCurrentLevelProgress;
    },
    currentUser() {
      return this.$store.getters.getUser?.userId;
    },
  },
  async mounted() {
    await this.$store.dispatch('fetchSuperUserProfileInfo', {
      userId: this.data.userData._id,
      lang: this.lang,
    });
  },
  methods: {
    formatDate(originalDate) {
      const formatted = formatLocaleDate(originalDate);
      return formatted || ' ';
    },
    async deleteUser() {
      this.showPopup = true;
      this.$confirm.require({
        message: this.popupMessage,
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
          label: this.cancelButtonText,
          severity: 'secondary',
          outlined: true,
        },
        acceptProps: {
          label: this.deleteButtonText,
        },
        accept: this.onPopupConfirm,
        reject: this.onPopupCancel,
      });
    },
    onPopupCancel() {
      this.showPopup = false;
    },
    async onPopupConfirm() {
      this.showPopup = false;
      if (this.getSuperUserProfileInfo._id === this.currentUser) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: this.$t('UserAdministrationComponent.toast.deleteSelfError'),
        });
        return;
      }
      const data = {};
      data.userIDs = [this.getSuperUserProfileInfo._id];

      try {
        this.showPopup = false;
        await this.$store.dispatch('deleteUser', data);
        await this.$store.dispatch('getAllFilteredUsers');
        this.$toast.add({
          severity: 'success',
          summary: this.$t('UserAdministrationComponent.toast.successfulDelete'),
          detail: '',
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('UserAdministrationComponent.toast.errorDelete'),
          detail: '',
          life: 3000,
        });
      }
      this.$emit('update:visible', false);
    },
    showIdentifier(tag) {
      this.showTagIdentifier.push(tag);
    },
    hideIdentifier(tag) {
      this.showTagIdentifier = this.showTagIdentifier.filter((t) => t._id !== tag._id);
    },
    showTaskHover(task) {
      this.showTasksHover.push(task);
    },
    hideTaskHover(task) {
      this.showTasksHover = this.showTasksHover.filter((t) => t._id !== task._id);
    },
  },
};
</script>

<style scoped>
.card {
  @apply text-xs py-1 px-3 border-[var(--color-orange)];
}
</style>
